import React, { useState } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Textarea,
  Alert
} from '@cloudscape-design/components';
import config from '../config';

const ContactModal = ({ visible, onDismiss, theme = 'dark' }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async () => {
    // Validation
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(config.ContactFormFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          company: company.trim(),
          message: message.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Reset form
        setName('');
        setEmail('');
        setCompany('');
        setMessage('');
        
        // Close modal after 2 seconds
        setTimeout(() => {
          setSuccess(false);
          onDismiss();
        }, 2000);
      } else {
        setError(data.message || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setError('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName('');
      setEmail('');
      setCompany('');
      setMessage('');
      setError('');
      setSuccess(false);
      onDismiss();
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleClose}
      header="Contact Sales"
      closeAriaLabel="Close modal"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button 
              variant="link" 
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={success}
            >
              Send Message
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert type="success">
            Thank you! We'll get back to you shortly.
          </Alert>
        )}

        <FormField
          label="Name"
          description="Your full name"
          stretch
        >
          <Input
            value={name}
            onChange={({ detail }) => setName(detail.value)}
            placeholder="John Doe"
            disabled={isSubmitting || success}
          />
        </FormField>

        <FormField
          label="Email"
          description="We'll use this to respond to you"
          stretch
        >
          <Input
            value={email}
            onChange={({ detail }) => setEmail(detail.value)}
            placeholder="john@company.com"
            type="email"
            disabled={isSubmitting || success}
          />
        </FormField>

        <FormField
          label="Company"
          description="Optional - helps us understand your needs"
          stretch
        >
          <Input
            value={company}
            onChange={({ detail }) => setCompany(detail.value)}
            placeholder="Acme Corp"
            disabled={isSubmitting || success}
          />
        </FormField>

        <FormField
          label="Message"
          description="Tell us about your requirements"
          stretch
        >
          <Textarea
            value={message}
            onChange={({ detail }) => setMessage(detail.value)}
            placeholder="I'm interested in enterprise deployment for my organization..."
            rows={6}
            disabled={isSubmitting || success}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default ContactModal;
